import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { invoicesAPI, offersAPI, freeInvoicesAPI, emailTrackingAPI, legacyOffersAPI, freeOffersAPI, api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import { EmailStatusBadge } from "../components/EmailTracking";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { SortableTableHead, useTableSort } from "../components/ui/sortable-table-head";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  FileText,
  Plus,
  Eye,
  Download,
  MoreVertical,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Shield,
  FileX,
  Loader2,
  FileCheck,
  History,
  Search,
  Filter,
  Mail,
  Send,
  PenLine,
  Calendar,
  RotateCcw,
  Unlock,
  ChevronsUpDown,
  Check,
  RefreshCw
} from "lucide-react";

const STATUS_CONFIG = {
  entwurf: { label: "Entwurf", color: "bg-yellow-100 text-yellow-800", icon: FileText },
  final: { label: "Finalisiert", color: "bg-green-100 text-green-800", icon: CheckCircle },
  finalisiert: { label: "Finalisiert", color: "bg-green-100 text-green-800", icon: CheckCircle },
  bezahlt: { label: "Bezahlt", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  storno: { label: "Storniert", color: "bg-red-100 text-red-800", icon: XCircle },
  storniert: { label: "Storniert", color: "bg-red-100 text-red-800", icon: XCircle },
  gesendet: { label: "Gesendet", color: "bg-blue-100 text-blue-800", icon: Send }
};

const TYPE_CONFIG = {
  abschlagsrechnung: { label: "Abschlagsrechnung", color: "bg-blue-100 text-blue-800" },
  schlussrechnung: { label: "Schlussrechnung", color: "bg-purple-100 text-purple-800" },
  stornorechnung: { label: "Stornorechnung", color: "bg-red-100 text-red-800" },
  freitext: { label: "Freitext", color: "bg-amber-100 text-amber-800" }
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [auditLogDialogOpen, setAuditLogDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Quarterly PDF-export dialog state
  const [qExportOpen, setQExportOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
  const [qExportYear, setQExportYear] = useState(currentYear);
  const [qExportQuarter, setQExportQuarter] = useState(currentQuarter);
  const [qExporting, setQExporting] = useState(false);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [exportDateTo, setExportDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [exportIncludeCancelled, setExportIncludeCancelled] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Sorting state - default to newest first
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort("created_at", "desc");
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    offer_id: "",
    invoice_type: "abschlagsrechnung",
    partial_number: 1,
    notes: ""
  });
  const [creating, setCreating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [unlocking, setUnlocking] = useState(null);
  const [cancellingFree, setCancellingFree] = useState(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState({
    // For regular invoices
    notes: "",
    delivery_date: "",
    payment_due_days: 14,
    gross_total_override: "",  // Manuelle Betragskorrektur
    // === SCHLUSSRECHNUNG: Erweiterte Felder ===
    calculation_mode: "auto",  // "auto" oder "manual"
    manual_total_amount: "",  // Manueller GESAMTBETRAG (nach Nachlass)
    manual_adjustment_reason: "",  // Begründung (z.B. "Nachträglicher Nachlass")
    // For free invoices
    title: "",
    items: [],
    include_vat: true,
    vat_rate: 19.0
  });
  const [saving, setSaving] = useState(false);
  const [offerPopoverOpen, setOfferPopoverOpen] = useState(false);

  useEffect(() => {
    loadInvoices();
    loadOffers();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      // Load both regular and free-text invoices
      const [regularResponse, freeResponse] = await Promise.all([
        invoicesAPI.list(),
        freeInvoicesAPI.list().catch(() => ({ data: [] })) // Handle if free invoices API fails
      ]);
      
      // Mark free invoices with a flag and normalize structure
      const freeInvoices = (freeResponse.data || []).map(inv => ({
        ...inv,
        is_free_invoice: true,
        invoice_type: 'freitext',
        offer_number: '-',
        offer_id: null
      }));
      
      // Combine and sort by created_at
      const allInvoices = [...regularResponse.data, ...freeInvoices].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      
      setInvoices(allInvoices);
    } catch (error) {
      console.error("Failed to load invoices:", error);
      toast.error("Fehler beim Laden der Rechnungen");
    } finally {
      setLoading(false);
    }
  };

  const loadOffers = async () => {
    try {
      // Load native PV offers, legacy (PDF-Import) offers and free-text offers in parallel
      const [offersRes, legacyRes, freeRes] = await Promise.all([
        offersAPI.list(),
        legacyOffersAPI.list().catch(() => ({ data: [] })),
        freeOffersAPI.list().catch(() => ({ data: [] }))
      ]);

      // Native/Imported PV: signed OR manually accepted OR PDF-imported
      const nativeEligible = (offersRes.data || [])
        .filter(o =>
          o.signature ||
          o.manually_marked_accepted_at ||
          o.status === "angenommen" ||
          o.status === "accepted" ||
          o.is_legacy_import === true
        )
        .map(o => ({
          id: o.id,
          offer_number: o.offer_number,
          customer_name: o.customer_name,
          total_price: o.total_price || o.total_gross,
          created_at: o.created_at,
          _source: o.is_legacy_import === true ? "imported" : "native"
        }));

      // Legacy: status must allow invoicing (freigegeben, beauftragt, teilfakturiert)
      const legacyEligibleStatuses = ["freigegeben", "beauftragt", "teilfakturiert"];
      const legacyList = Array.isArray(legacyRes.data) ? legacyRes.data : (legacyRes.data?.data || []);
      const legacyEligible = legacyList
        .filter(o => legacyEligibleStatuses.includes(o.status))
        .map(o => ({
          id: o.id,
          offer_number: o.offer_reference || `LEG-${o.id.slice(0, 6)}`,
          customer_name: o.customer_name,
          total_price: o.gross_amount,
          created_at: o.created_at,
          _source: "legacy"
        }));

      // Free-text offers: status erstellt or accepted/angenommen (free offers don't always require signature)
      const freeList = Array.isArray(freeRes.data) ? freeRes.data : (freeRes.data?.data || []);
      const freeEligible = freeList
        .filter(o => o.status !== "storniert" && o.status !== "cancelled")
        .map(o => ({
          id: o.id,
          offer_number: o.offer_number,
          customer_name: o.customer_name,
          total_price: o.total,
          created_at: o.created_at,
          _source: "free"
        }));

      // Combine and sort newest first
      const combined = [...nativeEligible, ...legacyEligible, ...freeEligible].sort((a, b) => {
        const dateA = new Date(a.created_at || 0).getTime();
        const dateB = new Date(b.created_at || 0).getTime();
        return dateB - dateA;
      });
      setOffers(combined);
    } catch (error) {
      console.error("Failed to load offers:", error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    return new Date(isoDate).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const handleCreateInvoice = async () => {
    if (!createForm.offer_id) {
      toast.error("Bitte wählen Sie ein Angebot");
      return;
    }
    
    setCreating(true);
    try {
      const response = await invoicesAPI.create(createForm);
      toast.success("Rechnungsentwurf erstellt");
      setCreateDialogOpen(false);
      setCreateForm({
        offer_id: "",
        invoice_type: "abschlagsrechnung",
        partial_number: 1,
        notes: ""
      });
      loadInvoices();
    } catch (error) {
      console.error("Failed to create invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setCreating(false);
    }
  };

  const handleFinalizeInvoice = async (invoice) => {
    const isDraft = invoice.status === "entwurf" || invoice.status === "draft" || invoice.status === "Entwurf";
    if (!isDraft) return;
    
    if (!window.confirm(
      "Sind Sie sicher? Nach der Finalisierung kann die Rechnung nicht mehr bearbeitet werden (GoBD-konform)."
    )) return;
    
    setFinalizing(true);
    try {
      // Use the correct API based on invoice type
      const api = invoice.is_free_invoice ? freeInvoicesAPI : invoicesAPI;
      const response = await api.finalize(invoice.id);
      toast.success(`Rechnung ${response.data?.invoice_number || invoice.invoice_number} finalisiert`);
      loadInvoices();
    } catch (error) {
      console.error("Failed to finalize invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Finalisieren");
    } finally {
      setFinalizing(false);
    }
  };

  const handleCancelInvoice = async (invoice) => {
    // Free invoices have their own cancel endpoint now
    if (invoice.is_free_invoice) {
      await handleCancelFreeInvoice(invoice);
      return;
    }
    
    if (invoice.status !== "final") return;
    
    if (!window.confirm(
      `Möchten Sie die Rechnung ${invoice.invoice_number} wirklich stornieren? Es wird eine Stornorechnung erstellt.`
    )) return;
    
    setCancelling(true);
    try {
      const response = await invoicesAPI.cancel(invoice.id);
      toast.success(`Stornorechnung ${response.data.cancellation_invoice_number} erstellt`);
      loadInvoices();
    } catch (error) {
      console.error("Failed to cancel invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Stornieren");
    } finally {
      setCancelling(false);
    }
  };

  // Cancel a free invoice (mark as cancelled)
  const handleCancelFreeInvoice = async (invoice) => {
    if (!["finalisiert", "gesendet", "storniert"].includes(invoice.status)) {
      toast.error("Für diese Rechnung kann keine Stornorechnung erstellt werden");
      return;
    }
    
    const isRetroactive = invoice.status === "storniert";
    const promptText = isRetroactive
      ? `Für die bereits stornierte Rechnung ${invoice.invoice_number} wird nachträglich eine Stornorechnung mit eigener Nummer erstellt.\n\nGrund (optional):`
      : `Möchten Sie die Rechnung ${invoice.invoice_number} wirklich stornieren?\n\nEs wird eine Stornorechnung mit eigener Rechnungsnummer erstellt.\n\nGrund für die Stornierung (optional):`;
    
    const reason = window.prompt(promptText, isRetroactive ? "Nachträgliche Stornorechnung" : "Korrektur erforderlich");
    
    if (reason === null) return; // User cancelled
    
    setCancellingFree(invoice.id);
    try {
      const response = await freeInvoicesAPI.cancel(invoice.id, reason);
      const data = response.data;
      if (data.cancellation_invoice_number) {
        toast.success(`Stornorechnung ${data.cancellation_invoice_number} erstellt`);
      } else {
        toast.success(`Rechnung ${invoice.invoice_number} wurde storniert`);
      }
      loadInvoices();
    } catch (error) {
      console.error("Failed to cancel free invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Stornieren");
    } finally {
      setCancellingFree(null);
    }
  };

  // Unlock a finalized invoice for editing (Superadmin only)
  // Works for both free invoices AND regular project invoices
  const handleUnlockInvoice = async (invoice) => {
    const isFinalized = invoice.is_free_invoice 
      ? ["finalisiert", "gesendet"].includes(invoice.status)
      : ["final", "gesendet"].includes(invoice.status);
    
    if (!isFinalized) {
      toast.error("Nur finalisierte oder gesendete Rechnungen können entsperrt werden");
      return;
    }
    
    if (!window.confirm(
      `Möchten Sie die Rechnung ${invoice.invoice_number} wirklich entsperren?\n\nDie Rechnung kann dann bearbeitet und erneut finalisiert werden.`
    )) return;
    
    setUnlocking(invoice.id);
    try {
      // Use the correct API based on invoice type
      if (invoice.is_free_invoice) {
        await freeInvoicesAPI.unlock(invoice.id);
      } else {
        await invoicesAPI.unlock(invoice.id);
      }
      toast.success(`Rechnung ${invoice.invoice_number} wurde zur Bearbeitung entsperrt`);
      loadInvoices();
    } catch (error) {
      console.error("Failed to unlock invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Entsperren");
    } finally {
      setUnlocking(null);
    }
  };

  // Recalculate a final invoice (Schlussrechnung) - fixes incorrect calculations
  const [recalculating, setRecalculating] = useState(null);
  
  const handleRecalculateFinal = async (invoice) => {
    if (invoice.status !== "entwurf" && invoice.status !== "draft") {
      toast.error("Nur Entwürfe können neu berechnet werden");
      return;
    }
    
    if (invoice.invoice_type !== "schlussrechnung" && invoice.invoice_type !== "schluss") {
      toast.error("Diese Funktion ist nur für Schlussrechnungen verfügbar");
      return;
    }
    
    if (!window.confirm(
      `Möchten Sie die Schlussrechnung ${invoice.invoice_number || 'Entwurf'} neu berechnen?\n\n` +
      `Dies wird:\n` +
      `• Den korrekten Angebotswert ermitteln\n` +
      `• Alle finalisierten Abschlagsrechnungen summieren\n` +
      `• Den Restbetrag neu berechnen`
    )) return;
    
    setRecalculating(invoice.id);
    try {
      const response = await invoicesAPI.recalculateFinal(invoice.id);
      toast.success(
        `Schlussrechnung neu berechnet:\n` +
        `Auftragswert: ${response.data.offer_total_gross?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €\n` +
        `Abschläge: ${response.data.previous_payments?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €\n` +
        `Restbetrag: ${response.data.remaining_amount?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`
      );
      loadInvoices();
    } catch (error) {
      console.error("Failed to recalculate invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Neuberechnen");
    } finally {
      setRecalculating(null);
    }
  };

  const handleMarkAsPaid = async (invoice) => {
    // For free invoices, check for "finalisiert" status; for regular invoices, check for "final"
    const isPaidableStatus = invoice.is_free_invoice 
      ? invoice.status === "finalisiert" 
      : invoice.status === "final";
    
    if (!isPaidableStatus) {
      toast.error("Nur finalisierte Rechnungen können als bezahlt markiert werden");
      return;
    }
    
    setMarkingPaid(invoice.id);
    try {
      // Use the correct API based on invoice type
      if (invoice.is_free_invoice) {
        await freeInvoicesAPI.markPaid(invoice.id);
      } else {
        await invoicesAPI.markAsPaid(invoice.id);
      }
      toast.success(`Rechnung ${invoice.invoice_number} als bezahlt markiert`);
      loadInvoices();
    } catch (error) {
      console.error("Failed to mark invoice as paid:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Markieren als bezahlt");
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleExportAll = async () => {
    setExportDialogOpen(true);
  };

  const handleQuarterlyPdfExport = async () => {
    setQExporting(true);
    try {
      const response = await api.get(`/invoices/quarterly-export`, {
        params: { year: qExportYear, quarter: qExportQuarter },
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ausgangsrechnungen_Q${qExportQuarter}_${qExportYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const count = response.headers["x-included-invoices"] || "?";
      const skippedCount = parseInt(response.headers["x-skipped-count"] || "0", 10);
      const skippedList = response.headers["x-skipped-invoices"] || "";
      const gaps = response.headers["x-number-gaps"] || "";
      if (skippedCount > 0) {
        toast.warning(
          `Quartals-PDF: ${count} Rechnungen enthalten, ${skippedCount} übersprungen: ${skippedList}`,
          { duration: 15000 }
        );
      } else {
        toast.success(`Quartals-PDF erstellt (${count} Rechnungen)`);
      }
      if (gaps) {
        toast.warning(`Nummernlücken erkannt: ${gaps}`, { duration: 15000 });
      }
      setQExportOpen(false);
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        toast.error(`Keine finalisierten Rechnungen für Q${qExportQuarter}/${qExportYear} gefunden`);
      } else {
        toast.error(error.response?.data?.detail || "Fehler beim Quartals-Export");
      }
    } finally {
      setQExporting(false);
    }
  };

  // Vorschau: was WÜRDE im Quartal enthalten sein? Nützlich, um vor dem Download
  // Vollständigkeit + Nummernlücken zu prüfen.
  const [qPreview, setQPreview] = useState(null);
  const [qPreviewLoading, setQPreviewLoading] = useState(false);
  const handleQuarterlyPreview = async () => {
    setQPreviewLoading(true);
    try {
      const res = await api.get(`/invoices/quarterly-export/preview`, {
        params: { year: qExportYear, quarter: qExportQuarter },
      });
      setQPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler bei Vorschau");
    } finally {
      setQPreviewLoading(false);
    }
  };

  const handleExportWithDateRange = async () => {
    try {
      setExporting(true);
      const response = await invoicesAPI.exportWithDateRange({
        date_from: exportDateFrom,
        date_to: exportDateTo,
        include_cancelled: exportIncludeCancelled
      });
      
      // Create CSV from response data
      const invoiceData = response.data.invoices || [];
      const csvHeader = 'Rechnungsnummer,Typ,Status,Kunde,Datum,Netto,MwSt,Brutto,Bezahlt am,Storniert,Erstellt am\n';
      const csvRows = invoiceData.map(inv => {
        const customer = inv.customer || {};
        return [
          inv.invoice_number || 'Entwurf',
          inv.invoice_type || '',
          inv.status || '',
          `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
          inv.invoice_date || '',
          (inv.net_total || 0).toFixed(2).replace('.', ','),
          (inv.vat_total || 0).toFixed(2).replace('.', ','),
          (inv.gross_total || 0).toFixed(2).replace('.', ','),
          inv.paid_at || '',
          inv.cancelled_at || '',
          inv.created_at || ''
        ].join(';');
      }).join('\n');
      
      const csvContent = csvHeader + csvRows;
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rechnungen_${exportDateFrom}_bis_${exportDateTo}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      // Show summary
      toast.success(`Export erfolgreich: ${response.data.invoice_count} Rechnungen`);
      if (response.data.total_gross) {
        toast.info(`Gesamtbetrag: ${response.data.total_gross.toFixed(2).replace('.', ',')} €`);
      }
      
      setExportDialogOpen(false);
    } catch (error) {
      console.error("Failed to export:", error);
      toast.error("Fehler beim Export");
    } finally {
      setExporting(false);
    }
  };

  const handleVerifyInvoice = async (invoice) => {
    if (invoice.status === "entwurf") {
      toast.error("Entwürfe können nicht verifiziert werden");
      return;
    }
    
    try {
      const response = await invoicesAPI.verify(invoice.id);
      setVerifyResult(response.data);
      setSelectedInvoice(invoice);
      setVerifyDialogOpen(true);
    } catch (error) {
      console.error("Failed to verify invoice:", error);
      toast.error("Fehler bei der Verifizierung");
    }
  };

  const handleViewAuditLog = async (invoice) => {
    try {
      const response = await invoicesAPI.getAuditLog({ entity_id: invoice.id });
      setAuditLogs(response.data);
      setSelectedInvoice(invoice);
      setAuditLogDialogOpen(true);
    } catch (error) {
      console.error("Failed to load audit log:", error);
      toast.error("Fehler beim Laden des Audit-Logs");
    }
  };

  // Edit invoice handlers
  const handleOpenEditDialog = (invoice) => {
    setEditingInvoice(invoice);
    if (invoice.is_free_invoice) {
      // Free invoice
      setEditForm({
        title: invoice.title || "",
        items: invoice.items || [],
        payment_days: invoice.payment_days || 14,
        include_vat: invoice.include_vat !== false,
        vat_rate: invoice.vat_rate ?? 19.0,
        notes: invoice.notes || "",
        show_item_prices: invoice.show_item_prices !== false,
        // External advances (paid outside CRM)
        external_advances: Array.isArray(invoice.external_advances_snapshot)
          ? invoice.external_advances_snapshot.map(e => ({
              invoice_number: e.invoice_number || "",
              amount: e.amount || 0,
              payment_date: e.payment_date || "",
              notes: e.notes || "",
            }))
          : [],
        total: invoice.total || 0,
        // Ausführungsadresse (Leistungsort) – optional, editierbar in Entwurf
        execution_address: invoice.execution_address || { name: "", street: "", zip: "", city: "" },
        // Regular invoice fields (not used for free)
        delivery_date: "",
        payment_due_days: 14,
        gross_total_override: ""
      });
    } else {
      // Regular invoice
      setEditForm({
        notes: invoice.notes || "",
        delivery_date: invoice.delivery_date || "",
        payment_due_days: invoice.payment_due_days || 14,
        gross_total_override: "",  // Leave empty to show placeholder with current value
        current_gross_total: invoice.gross_total || 0,  // Store current value for display
        // === SCHLUSSRECHNUNG: Erweiterte Felder ===
        calculation_mode: invoice.calculation_mode || "auto",
        manual_total_amount: invoice.manual_total_amount || "",  // Manueller GESAMTBETRAG
        manual_adjustment_reason: invoice.manual_adjustment_reason || "",
        offer_total_gross: invoice.offer_total_gross || invoice.gross_total || 0,
        previous_payments: invoice.previous_payments || 0,
        remaining_amount: invoice.remaining_amount || invoice.gross_total || 0,
        has_overpayment_warning: invoice.has_overpayment_warning || false,
        invoice_type: invoice.invoice_type || "",
        // External advances (paid outside CRM)
        external_advances: Array.isArray(invoice.external_advances_snapshot)
          ? invoice.external_advances_snapshot.map(e => ({
              invoice_number: e.invoice_number || "",
              amount: e.amount || 0,
              payment_date: e.payment_date || "",
              notes: e.notes || "",
            }))
          : [],
        // Free invoice fields (not used for regular)
        title: "",
        items: [],
        include_vat: true,
        vat_rate: 19.0,
        show_item_prices: invoice.show_item_prices !== false,
        // MwSt-Ausweis-Override für Non-PV Rechnungen (undefined = kein Change)
        include_vat_override: invoice.include_vat !== undefined ? invoice.include_vat : true,
        // Nullbetrag-Schlussrechnung (§35a EStG)
        force_zero_balance: !!invoice.force_zero_balance,
        labor_cost_percentage: invoice.labor_cost_percentage ?? "",
        // Schlussauflistung: manuell überarbeitbare Auflistung bereits gestellter Rechnungen
        previous_invoices_snapshot: Array.isArray(invoice.previous_invoices_snapshot)
          ? invoice.previous_invoices_snapshot.map(e => ({
              invoice_number: e.invoice_number || "",
              invoice_date: e.invoice_date || "",
              gross_total: e.gross_total || 0,
              invoice_type: e.invoice_type || "abschlagsrechnung",
            }))
          : []
      });
    }
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingInvoice) return;
    
    setSaving(true);
    try {
      if (editingInvoice.is_free_invoice) {
        // Update free invoice
        const freeUpdate = {
          title: editForm.title,
          items: editForm.items,
          payment_days: editForm.payment_days,
          include_vat: editForm.include_vat,
          vat_rate: editForm.vat_rate,
          notes: editForm.notes,
          show_item_prices: editForm.show_item_prices !== false
        };
        // Ausführungsadresse (nur senden, wenn im Form vorhanden – leere Adresse = null → wird gelöscht)
        if (editForm.execution_address !== undefined) {
          const ea = editForm.execution_address || {};
          const hasContent = !!(String(ea.name || "").trim() || String(ea.street || "").trim() ||
                                String(ea.zip || "").trim() || String(ea.city || "").trim());
          freeUpdate.execution_address = hasContent ? {
            name: String(ea.name || "").trim(),
            street: String(ea.street || "").trim(),
            zip: String(ea.zip || "").trim(),
            city: String(ea.city || "").trim(),
          } : {};
        }
        if (Array.isArray(editForm.external_advances)) {
          freeUpdate.external_advances = editForm.external_advances
            .filter(a => a.invoice_number && parseFloat(a.amount) > 0)
            .map(a => ({
              invoice_number: String(a.invoice_number).trim(),
              amount: parseFloat(a.amount),
              payment_date: a.payment_date || null,
              notes: a.notes || null,
            }));
        }
        await freeInvoicesAPI.update(editingInvoice.id, freeUpdate);
      } else {
        // Update regular invoice
        const updateData = {
          notes: editForm.notes,
          delivery_date: editForm.delivery_date || null,
          payment_due_days: editForm.payment_due_days,
          show_item_prices: editForm.show_item_prices !== false
        };
        
        // MwSt-Ausweis Toggle: nur senden, wenn Switch tatsächlich sichtbar ist
        // (Non-PV Rechnungen). Bei PV-Rechnungen ist include_vat_override immer null.
        const items = editingInvoice?.line_items || [];
        const hasPvSemantics = items.some(it =>
          ["pv_system", "wallbox", "storage", "inverter"].includes(it.component_type)
        );
        if (!hasPvSemantics && editForm.include_vat_override !== undefined && editForm.include_vat_override !== null) {
          updateData.include_vat_override = editForm.include_vat_override;
        }
        
        // Add gross_total_override if provided (for non-Schlussrechnung)
        if (editForm.gross_total_override && parseFloat(editForm.gross_total_override) > 0) {
          updateData.gross_total_override = parseFloat(editForm.gross_total_override);
        }
        
        // === SCHLUSSRECHNUNG: Manuelle Gesamtbetragskorrektur ===
        if (editingInvoice.invoice_type === "schlussrechnung" || editingInvoice.invoice_type === "schluss") {
          updateData.calculation_mode = editForm.calculation_mode;
          
          if (editForm.calculation_mode === "manual") {
            if (!editForm.manual_total_amount || parseFloat(editForm.manual_total_amount) <= 0) {
              toast.error("Bitte geben Sie einen gültigen Gesamtbetrag ein (> 0)");
              setSaving(false);
              return;
            }
            updateData.manual_total_amount = parseFloat(editForm.manual_total_amount);
            updateData.manual_adjustment_reason = editForm.manual_adjustment_reason || null;
          } else if (editForm.calculation_mode === "auto") {
            // Reset manual values
            updateData.manual_total_amount = null;
            updateData.manual_adjustment_reason = null;
          }

          // Externe Abschlagsrechnungen (außerhalb CRM gezahlt)
          if (Array.isArray(editForm.external_advances)) {
            updateData.external_advances = editForm.external_advances
              .filter(a => a.invoice_number && parseFloat(a.amount) > 0)
              .map(a => ({
                invoice_number: String(a.invoice_number).trim(),
                amount: parseFloat(a.amount),
                payment_date: a.payment_date || null,
                notes: a.notes || null,
              }));
          }

          // Nullbetrag-Schlussrechnung + Lohnkostenanteil (§35a EStG)
          if (typeof editForm.force_zero_balance === "boolean") {
            updateData.force_zero_balance = editForm.force_zero_balance;
          }
          if (editForm.labor_cost_percentage !== undefined && editForm.labor_cost_percentage !== null && editForm.labor_cost_percentage !== "") {
            const _pct = parseFloat(editForm.labor_cost_percentage);
            if (!isNaN(_pct)) updateData.labor_cost_percentage = _pct;
          }
          // Schlussauflistung: manuell bearbeitete Auflistung übertragen
          if (Array.isArray(editForm.previous_invoices_snapshot)) {
            updateData.previous_invoices_snapshot = editForm.previous_invoices_snapshot
              .filter(e => e.invoice_number && parseFloat(e.gross_total) > 0)
              .map(e => ({
                invoice_number: String(e.invoice_number).trim(),
                invoice_date: e.invoice_date || null,
                gross_total: parseFloat(e.gross_total),
                invoice_type: e.invoice_type || "abschlagsrechnung",
              }));
          }
        }
        
        await invoicesAPI.update(editingInvoice.id, updateData);
      }
      
      toast.success("Rechnung aktualisiert");
      setEditDialogOpen(false);
      setEditingInvoice(null);
      loadInvoices();
    } catch (error) {
      console.error("Failed to update invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = () => {
    setEditForm(prev => ({
      ...prev,
      items: [...prev.items, { description: "", quantity: 1, unit: "Stück", unit_price: 0, total: 0 }]
    }));
  };

  const handleUpdateItem = (index, field, value) => {
    setEditForm(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      // Recalculate total
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
      return { ...prev, items: newItems };
    });
  };

  const handleRemoveItem = (index) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleViewPdf = (invoice) => {
    if (invoice.is_free_invoice) {
      // For free-text invoices, use different API
      const url = freeInvoicesAPI.getPdfUrl(invoice.id);
      window.open(url, "_blank");
      return;
    }
    if (invoice.status === "entwurf") {
      // For drafts, use preview endpoint
      const url = invoicesAPI.getPreviewPdfUrl(invoice.id);
      window.open(url, "_blank");
      return;
    }
    const url = invoicesAPI.getPdfUrl(invoice.id);
    window.open(url, "_blank");
  };

  const handlePreviewDraft = (invoice) => {
    if (invoice.is_free_invoice) {
      const url = freeInvoicesAPI.getPdfUrl(invoice.id);
      window.open(url, "_blank");
      return;
    }
    const url = invoicesAPI.getPreviewPdfUrl(invoice.id);
    window.open(url, "_blank");
  };

  const handleSendEmail = async (invoice) => {
    // Check status - free invoices use "finalisiert", regular invoices use "final"
    const isFinalized = invoice.is_free_invoice 
      ? (invoice.status === "finalisiert" || invoice.status === "bezahlt")
      : invoice.status === "final";
    
    if (!isFinalized) {
      toast.error("Nur finalisierte Rechnungen können versendet werden");
      return;
    }
    
    if (invoice.sent_at) {
      if (!window.confirm(
        `Diese Rechnung wurde bereits am ${formatDate(invoice.sent_at)} versendet. Möchten Sie sie erneut senden?`
      )) return;
    } else {
      if (!window.confirm(
        `Möchten Sie die Rechnung ${invoice.invoice_number} per E-Mail an den Kunden senden?`
      )) return;
    }
    
    setSendingEmail(true);
    try {
      // Use the correct API based on invoice type
      const api = invoice.is_free_invoice ? freeInvoicesAPI : invoicesAPI;
      const response = await api.sendEmail(invoice.id);
      toast.success(response.data.message || "Rechnung erfolgreich versendet");
      loadInvoices();
    } catch (error) {
      console.error("Failed to send invoice email:", error);
      toast.error(error.response?.data?.detail || "Fehler beim E-Mail-Versand");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadXml = (invoice) => {
    if (invoice.status === "entwurf") {
      toast.error("Entwürfe haben kein XML. Bitte zuerst finalisieren.");
      return;
    }
    const url = invoicesAPI.getXmlUrl(invoice.id);
    window.open(url, "_blank");
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(invoice => {
    const matchesStatus = filterStatus === "all" || invoice.status === filterStatus;
    const matchesSearch = !searchTerm || 
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.offer_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Apply sorting
  const sortedInvoices = sortData(filteredInvoices);

  // Calculate totals
  const totals = {
    draft: invoices.filter(i => i.status === "entwurf").length,
    final: invoices.filter(i => i.status === "final" || i.status === "finalisiert").length,
    cancelled: invoices.filter(i => i.status === "storno" || i.status === "storniert").length,
    totalAmount: invoices
      .filter(i => (i.status === "final" || i.status === "finalisiert") && i.invoice_type !== "stornorechnung")
      .reduce((sum, i) => sum + (i.gross_total || i.total || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rechnungen</h1>
          <p className="text-sm text-gray-500 mt-1">
            GoBD-konforme E-Rechnungen mit ZUGFeRD
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportAll} data-testid="export-invoices-btn">
            <Download className="h-4 w-4 mr-2" />
            Export (CSV)
          </Button>
          <Button variant="outline" onClick={() => setQExportOpen(true)} data-testid="quarterly-pdf-export-btn">
            <Download className="h-4 w-4 mr-2" />
            Quartals-PDF
          </Button>
          {user?.role === "admin" && (
            <Button variant="outline" asChild data-testid="create-free-invoice-btn">
              <a href="/freitext-rechnungen/neu">
                <PenLine className="h-4 w-4 mr-2" />
                Freitext Rechnung
              </a>
            </Button>
          )}
          {(user?.role === "admin" || user?.role === "superadmin") && (
            <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-invoice-btn">
              <Plus className="h-4 w-4 mr-2" />
              Rechnung erstellen
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Entwürfe</p>
                <p className="text-2xl font-bold text-yellow-600">{totals.draft}</p>
              </div>
              <FileText className="h-8 w-8 text-yellow-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Finalisiert</p>
                <p className="text-2xl font-bold text-green-600">{totals.final}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Storniert</p>
                <p className="text-2xl font-bold text-red-600">{totals.cancelled}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gesamtvolumen</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(totals.totalAmount)}</p>
              </div>
              <FileCheck className="h-8 w-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Suche nach Rechnungsnummer, Kunde oder Angebot..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="invoice-search"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]" data-testid="invoice-status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="entwurf">Entwürfe</SelectItem>
                <SelectItem value="final">Finalisiert</SelectItem>
                <SelectItem value="storno">Storniert</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardHeader>
          <CardTitle>Rechnungsübersicht ({filteredInvoices.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedInvoices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Keine Rechnungen gefunden</p>
              {invoices.length === 0 && (
                <p className="text-sm mt-2">
                  Erstellen Sie Ihre erste Rechnung aus einem unterschriebenen Angebot
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    column="invoice_number"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Rechnungsnr.
                  </SortableTableHead>
                  <TableHead>Typ</TableHead>
                  <SortableTableHead
                    column="customer_name"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Kunde
                  </SortableTableHead>
                  <TableHead>Angebot</TableHead>
                  <SortableTableHead
                    column="created_at"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Datum
                  </SortableTableHead>
                  <SortableTableHead
                    column="gross_total"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="text-right"
                  >
                    Betrag
                  </SortableTableHead>
                  <SortableTableHead
                    column="status"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Status
                  </SortableTableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedInvoices.map((invoice) => {
                  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.entwurf;
                  const typeConfig = TYPE_CONFIG[invoice.invoice_type] || TYPE_CONFIG.abschlagsrechnung;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {invoice.invoice_number || (
                            <span className="text-gray-400 italic">Entwurf</span>
                          )}
                          <IncognitoBadge active={invoice.is_incognito} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={typeConfig.color}>
                          {typeConfig.label}
                          {invoice.partial_number && ` ${invoice.partial_number}`}
                        </Badge>
                        {Array.isArray(invoice.external_advances_snapshot) && invoice.external_advances_snapshot.length > 0 && (
                          <Badge
                            variant="outline"
                            className="ml-1 border-orange-300 bg-orange-50 text-orange-700 text-[10px]"
                            title={`Externe Abschläge: ${invoice.external_advances_snapshot.map(e => `${e.invoice_number} (${e.amount?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €)`).join(', ')}`}
                            data-testid={`badge-ext-advances-${invoice.id}`}
                          >
                            +{invoice.external_advances_snapshot.length} ext.
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{invoice.customer_name}</TableCell>
                      <TableCell className="text-gray-500">
                        {invoice.is_free_invoice ? (
                          <span className="text-amber-600 italic">Freitext</span>
                        ) : invoice.source_type === "legacy" || invoice.legacy_offer_id ? (
                          <span className="text-purple-600 italic">Legacy</span>
                        ) : (
                          invoice.offer_number
                        )}
                      </TableCell>
                      <TableCell>{formatDate(invoice.invoice_date || invoice.created_at)}</TableCell>
                      <TableCell className="text-right font-medium">
                        <div className="flex flex-col items-end">
                          {formatCurrency(invoice.gross_total || invoice.total)}
                          {/* Schlussrechnung: Zeige Restbetrag wenn abweichend */}
                          {(invoice.invoice_type === "schlussrechnung" || invoice.invoice_type === "schluss") && invoice.remaining_amount !== undefined && (
                            <span className={`text-xs ${invoice.has_overpayment_warning ? 'text-red-600' : 'text-muted-foreground'}`}>
                              {invoice.has_overpayment_warning && <AlertTriangle className="h-3 w-3 inline mr-1" />}
                              Zahlbetrag: {formatCurrency(invoice.remaining_amount)}</span>
                          )}
                          {/* Free-Invoice mit externen Abschlägen: Zahlbetrag anzeigen */}
                          {invoice.is_free_invoice && invoice.remaining_amount !== null && invoice.remaining_amount !== undefined && Array.isArray(invoice.external_advances_snapshot) && invoice.external_advances_snapshot.length > 0 && (
                            <span className="text-xs text-orange-600">
                              Zahlbetrag: {formatCurrency(invoice.remaining_amount)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          {/* Email delivery status - only show if email was sent */}
                          {invoice.sent_at && (
                            invoice.email_tracking_id ? (
                              // Show delivery tracking status if available
                              <EmailStatusBadge 
                                status={invoice.email_send_status || "sent"} 
                                size="small"
                                showLabel={false}
                              />
                            ) : (
                              // Fallback for emails sent before tracking was implemented
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                <Send className="h-3 w-3 mr-1" />
                                Versendet
                              </Badge>
                            )
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`invoice-actions-${invoice.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* PDF Actions */}
                            {invoice.status === "entwurf" ? (
                              <DropdownMenuItem 
                                onClick={() => handlePreviewDraft(invoice)}
                                data-testid={`preview-draft-${invoice.id}`}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                PDF-Vorschau
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem 
                                  onClick={() => handleViewPdf(invoice)}
                                  data-testid={`view-pdf-${invoice.id}`}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  PDF ansehen
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownloadXml(invoice)}>
                                  <Download className="h-4 w-4 mr-2" />
                                  XML herunterladen
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            {/* Edit Action - only for drafts and admins */}
                            {invoice.status === "entwurf" && (user?.role === "admin" || user?.role === "superadmin") && (
                              <DropdownMenuItem 
                                onClick={() => handleOpenEditDialog(invoice)}
                                data-testid={`edit-invoice-${invoice.id}`}
                              >
                                <PenLine className="h-4 w-4 mr-2" />
                                Bearbeiten
                              </DropdownMenuItem>
                            )}
                            
                            {/* Recalculate Final Invoice - only for draft Schlussrechnungen */}
                            {invoice.status === "entwurf" && 
                             (invoice.invoice_type === "schlussrechnung" || invoice.invoice_type === "schluss") && 
                             (user?.role === "admin" || user?.role === "superadmin") && (
                              <DropdownMenuItem 
                                onClick={() => handleRecalculateFinal(invoice)}
                                disabled={recalculating === invoice.id}
                                className="text-purple-600"
                                data-testid={`recalculate-${invoice.id}`}
                              >
                                <RefreshCw className={`h-4 w-4 mr-2 ${recalculating === invoice.id ? 'animate-spin' : ''}`} />
                                Neu berechnen
                                {recalculating === invoice.id && <Loader2 className="h-3 w-3 ml-2 animate-spin" />}
                              </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuSeparator />
                            
                            {/* Status Actions */}
                            {(invoice.status === "entwurf" || invoice.status === "draft" || invoice.status === "Entwurf") && (
                              <DropdownMenuItem 
                                onClick={() => handleFinalizeInvoice(invoice)}
                                disabled={finalizing}
                                data-testid={`finalize-${invoice.id}`}
                              >
                                <FileCheck className="h-4 w-4 mr-2" />
                                Finalisieren
                              </DropdownMenuItem>
                            )}
                            
                            {(invoice.status === "final" || invoice.status === "finalisiert" || invoice.status === "gesendet") && (
                              <>
                                <DropdownMenuItem 
                                  onClick={() => handleSendEmail(invoice)}
                                  disabled={sendingEmail}
                                  data-testid={`send-email-${invoice.id}`}
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  {invoice.sent_at ? "Erneut senden" : "Per E-Mail senden"}
                                  {sendingEmail && <Loader2 className="h-3 w-3 ml-2 animate-spin" />}
                                </DropdownMenuItem>
                                
                                {/* Cancel for regular invoices */}
                                {!invoice.cancellation_invoice_id && !invoice.is_free_invoice && (
                                  <DropdownMenuItem 
                                    onClick={() => handleCancelInvoice(invoice)}
                                    disabled={cancelling}
                                    className="text-red-600"
                                    data-testid={`cancel-${invoice.id}`}
                                  >
                                    <FileX className="h-4 w-4 mr-2" />
                                    Stornieren
                                  </DropdownMenuItem>
                                )}
                                
                                {/* Unlock for regular project invoices (Superadmin only) */}
                                {!invoice.is_free_invoice && user?.is_superadmin && 
                                  (invoice.status === "final" || invoice.status === "gesendet") && 
                                  invoice.status !== "storniert" && invoice.status !== "bezahlt" && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => handleUnlockInvoice(invoice)}
                                      disabled={unlocking === invoice.id}
                                      className="text-blue-600"
                                      data-testid={`unlock-project-${invoice.id}`}
                                    >
                                      <Unlock className="h-4 w-4 mr-2" />
                                      Zur Bearbeitung entsperren
                                      {unlocking === invoice.id && <Loader2 className="h-3 w-3 ml-2 animate-spin" />}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                
                                {/* Cancel and Unlock for free invoices */}
                                {invoice.is_free_invoice && (invoice.status !== "storniert" || !invoice.cancellation_invoice_id) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    {user?.is_superadmin && (invoice.status === "finalisiert" || invoice.status === "gesendet") && (
                                      <DropdownMenuItem 
                                        onClick={() => handleUnlockInvoice(invoice)}
                                        disabled={unlocking === invoice.id}
                                        className="text-blue-600"
                                        data-testid={`unlock-${invoice.id}`}
                                      >
                                        <Unlock className="h-4 w-4 mr-2" />
                                        Zur Bearbeitung entsperren
                                        {unlocking === invoice.id && <Loader2 className="h-3 w-3 ml-2 animate-spin" />}
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem 
                                      onClick={() => handleCancelFreeInvoice(invoice)}
                                      disabled={cancellingFree === invoice.id}
                                      className="text-red-600"
                                      data-testid={`cancel-free-${invoice.id}`}
                                    >
                                      <FileX className="h-4 w-4 mr-2" />
                                      {invoice.status === "storniert" ? "Stornorechnung erstellen" : "Stornieren"}
                                      {cancellingFree === invoice.id && <Loader2 className="h-3 w-3 ml-2 animate-spin" />}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                
                                {!invoice.paid_at && !invoice.cancellation_invoice_id && (
                                  <DropdownMenuItem 
                                    onClick={() => handleMarkAsPaid(invoice)}
                                    disabled={markingPaid === invoice.id}
                                    data-testid={`mark-paid-${invoice.id}`}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                    Als bezahlt markieren
                                    {markingPaid === invoice.id && <Loader2 className="h-3 w-3 ml-2 animate-spin" />}
                                  </DropdownMenuItem>
                                )}
                                
                                {invoice.paid_at && (
                                  <DropdownMenuItem disabled>
                                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                    Bezahlt am {formatDate(invoice.paid_at)}
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            
                            {/* Verification & Audit */}
                            {invoice.status !== "entwurf" && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleVerifyInvoice(invoice)}>
                                  <Shield className="h-4 w-4 mr-2" />
                                  Integrität prüfen
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            <DropdownMenuItem onClick={() => handleViewAuditLog(invoice)}>
                              <History className="h-4 w-4 mr-2" />
                              Audit-Log
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Neue Rechnung erstellen</DialogTitle>
            <DialogDescription>
              Erstellen Sie eine Rechnung aus einem unterschriebenen Angebot
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rechnungstyp *</Label>
              <Select 
                value={createForm.invoice_type} 
                onValueChange={(value) => setCreateForm({ ...createForm, invoice_type: value })}
              >
                <SelectTrigger data-testid="select-invoice-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="abschlagsrechnung">Abschlagsrechnung</SelectItem>
                  <SelectItem value="schlussrechnung">Schlussrechnung</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Bei importierten Angeboten: Wähle den Typ <strong>vor</strong> der Angebotsauswahl.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Angebot *</Label>
              <Popover open={offerPopoverOpen} onOpenChange={setOfferPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={offerPopoverOpen}
                    data-testid="select-offer"
                    className="w-full justify-between font-normal"
                  >
                    {createForm.offer_id
                      ? (() => {
                          const o = offers.find(x => x.id === createForm.offer_id);
                          return o
                            ? `${o.offer_number} - ${o.customer_name || "Kunde"} (${formatCurrency(o.total_price)})`
                            : "Angebot auswählen";
                        })()
                      : "Angebot auswählen"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-0"
                  align="start"
                >
                  <Command
                    filter={(value, search) => {
                      if (!search) return 1;
                      return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                    }}
                  >
                    <CommandInput
                      placeholder="Angebotsnr. oder Kunde suchen..."
                      data-testid="select-offer-search"
                    />
                    <CommandList className="max-h-[320px]">
                      <CommandEmpty>Kein Angebot gefunden.</CommandEmpty>
                      <CommandGroup>
                        {offers.map((offer) => {
                          const label = `${offer.offer_number} - ${offer.customer_name || "Kunde"} (${formatCurrency(offer.total_price)})`;
                          const searchValue = `${offer.offer_number} ${offer.customer_name || ""} ${offer.id}`.toLowerCase();
                          const isLegacy = offer._source === "legacy";
                          const isImported = offer._source === "imported";
                          const isFree = offer._source === "free";
                          return (
                            <CommandItem
                              key={`${offer._source}-${offer.id}`}
                              value={searchValue}
                              onSelect={() => {
                                const invoiceTypeParam = createForm.invoice_type === "schlussrechnung" ? "final" : "partial";
                                if (isLegacy) {
                                  // Legacy offers have their own invoice workflow on the detail page
                                  setOfferPopoverOpen(false);
                                  setCreateDialogOpen(false);
                                  navigate(`/legacy-angebote/${offer.id}?openInvoice=${invoiceTypeParam}`);
                                  return;
                                }
                                if (isImported) {
                                  // PDF-imported offers have their own workflow on /angebote-import/:id
                                  setOfferPopoverOpen(false);
                                  setCreateDialogOpen(false);
                                  navigate(`/angebote-import/${offer.id}?openInvoice=${invoiceTypeParam}`);
                                  return;
                                }
                                if (isFree) {
                                  // Free-text offers have their own invoice workflow
                                  setOfferPopoverOpen(false);
                                  setCreateDialogOpen(false);
                                  navigate(`/freitext-angebote/${offer.id}?openInvoice=${invoiceTypeParam}`);
                                  return;
                                }
                                setCreateForm({ ...createForm, offer_id: offer.id });
                                setOfferPopoverOpen(false);
                              }}
                              data-testid={`offer-option-${offer.offer_number}`}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${createForm.offer_id === offer.id ? "opacity-100" : "opacity-0"}`}
                              />
                              <span className="flex-1 truncate">{label}</span>
                              {isLegacy && (
                                <Badge variant="outline" className="ml-2 shrink-0 border-orange-300 bg-orange-50 text-orange-700 text-[10px]">
                                  PDF
                                </Badge>
                              )}
                              {isImported && (
                                <Badge variant="outline" className="ml-2 shrink-0 border-purple-300 bg-purple-50 text-purple-700 text-[10px]">
                                  Import
                                </Badge>
                              )}
                              {isFree && (
                                <Badge variant="outline" className="ml-2 shrink-0 border-green-300 bg-green-50 text-green-700 text-[10px]">
                                  Frei
                                </Badge>
                              )}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {createForm.invoice_type === "abschlagsrechnung" && (
              <div className="space-y-2">
                <Label>Abschlagsnummer *</Label>
                <Select 
                  value={String(createForm.partial_number)} 
                  onValueChange={(value) => setCreateForm({ ...createForm, partial_number: parseInt(value) })}
                >
                  <SelectTrigger data-testid="select-partial-number">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1. Abschlagsrechnung (60%)</SelectItem>
                    <SelectItem value="2">2. Abschlagsrechnung (35%)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Bei Vorkasse-Zahlungsart: 100% statt Teilbeträge
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notizen (optional)</Label>
              <Textarea
                placeholder="Interne Notizen zur Rechnung..."
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                data-testid="invoice-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateInvoice} disabled={creating} data-testid="confirm-create-invoice">
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Erstelle...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Entwurf erstellen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Integrity Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Integritätsprüfung
            </DialogTitle>
            <DialogDescription>
              GoBD-konforme Prüfung der Rechnung {selectedInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          
          {verifyResult && (
            <div className="space-y-4 py-4">
              <div className={`p-4 rounded-lg ${verifyResult.all_valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {verifyResult.all_valid ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  )}
                  <span className={`font-medium ${verifyResult.all_valid ? 'text-green-800' : 'text-red-800'}`}>
                    {verifyResult.all_valid ? 'Alle Prüfungen bestanden' : 'Integritätsfehler erkannt'}
                  </span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">PDF-Integrität</span>
                  <Badge className={verifyResult.pdf_valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {verifyResult.pdf_valid ? 'Gültig' : 'Ungültig'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">XML-Integrität (ZUGFeRD)</span>
                  <Badge className={verifyResult.xml_valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {verifyResult.xml_valid ? 'Gültig' : 'Ungültig'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Daten-Integrität</span>
                  <Badge className={verifyResult.data_valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {verifyResult.data_valid ? 'Gültig' : 'Ungültig'}
                  </Badge>
                </div>
              </div>
              
              <div className="text-xs text-gray-500 mt-4">
                <p>Prüfmethode: SHA-256 Hash-Vergleich</p>
                <p className="truncate">PDF-Hash: {verifyResult.pdf_hash_stored?.substring(0, 16)}...</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setVerifyDialogOpen(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditLogDialogOpen} onOpenChange={setAuditLogDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Audit-Log
            </DialogTitle>
            <DialogDescription>
              Unveränderliches Änderungsprotokoll für {selectedInvoice?.invoice_number || 'Entwurf'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto">
            {auditLogs.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Keine Einträge</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Zeitpunkt</TableHead>
                    <TableHead>Aktion</TableHead>
                    <TableHead>Benutzer</TableHead>
                    <TableHead>Rolle</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">
                        {new Date(log.timestamp).toLocaleString("de-DE")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {log.action === "created" && "Erstellt"}
                          {log.action === "updated" && "Bearbeitet"}
                          {log.action === "finalized" && "Finalisiert"}
                          {log.action === "cancelled" && "Storniert"}
                          {log.action === "created_cancellation" && "Storno erstellt"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{log.user_email}</TableCell>
                      <TableCell className="text-sm text-gray-500">{log.user_role}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setAuditLogDialogOpen(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quartals-PDF-Export Dialog */}
      <Dialog open={qExportOpen} onOpenChange={setQExportOpen}>
        <DialogContent className="max-w-md" data-testid="quarterly-pdf-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Quartals-Sammelexport (PDF)
            </DialogTitle>
            <DialogDescription>
              Alle finalisierten Ausgangsrechnungen des gewählten Quartals werden in einem PDF zusammengefasst.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label htmlFor="q-export-year">Jahr</Label>
              <select
                id="q-export-year"
                value={qExportYear}
                onChange={(e) => setQExportYear(parseInt(e.target.value, 10))}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="quarterly-export-year"
              >
                {Array.from({ length: currentYear - 2018 + 3 }, (_, i) => 2018 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="q-export-quarter">Quartal</Label>
              <select
                id="q-export-quarter"
                value={qExportQuarter}
                onChange={(e) => setQExportQuarter(parseInt(e.target.value, 10))}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="quarterly-export-quarter"
              >
                {[1, 2, 3, 4].map(q => (
                  <option key={q} value={q}>Q{q} ({['Jan-Mrz', 'Apr-Jun', 'Jul-Sep', 'Okt-Dez'][q - 1]})</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Enthalten sind klassische und Freitext-Rechnungen mit Status <strong>final/finalisiert</strong>, sortiert nach Rechnungsdatum und Nummer. Fehlt bei einer Rechnung das PDF, wird es beim Export automatisch neu erzeugt.
          </p>

          {/* Vorschau-Panel: zeigt, welche Rechnungen enthalten wären */}
          {qPreview && (
            <div className="p-3 rounded-lg border bg-slate-50 space-y-2 max-h-64 overflow-y-auto">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Enthalten: {qPreview.included_count}</span>
                {qPreview.skipped_count > 0 && (
                  <span className="text-red-600 font-medium">Übersprungen: {qPreview.skipped_count}</span>
                )}
              </div>
              {qPreview.number_gaps?.length > 0 && (
                <div className="text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded p-2">
                  ⚠ Nummernlücken erkannt: <strong>{qPreview.number_gaps.join(", ")}</strong>
                </div>
              )}
              {qPreview.included?.length > 0 && (
                <div className="text-xs space-y-0.5" data-testid="quarterly-preview-included">
                  <div className="font-medium text-muted-foreground">Enthaltene Rechnungen:</div>
                  {qPreview.included.map((r, i) => {
                    const st = r.status || "";
                    const stColor = st === "storniert" || st === "storno" ? "text-red-600" :
                                    st === "entwurf" ? "text-amber-600" : "text-emerald-700";
                    return (
                      <div key={i} className="flex justify-between font-mono">
                        <span>
                          {r.invoice_number || "(o. Nr.)"}{" "}
                          <span className="text-muted-foreground">[{r.source}]</span>
                          {st && <span className={`ml-1 ${stColor}`}>[{st}]</span>}
                        </span>
                        <span className="text-muted-foreground">{r.invoice_date}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {qPreview.skipped?.length > 0 && (
                <div className="text-xs space-y-0.5 border-t pt-2">
                  <div className="font-medium text-red-600">Übersprungene Rechnungen:</div>
                  {qPreview.skipped.map((r, i) => (
                    <div key={i} className="flex justify-between font-mono">
                      <span>{r.invoice_number}</span>
                      <span className="text-red-600">{r.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setQExportOpen(false); setQPreview(null); }} disabled={qExporting}>
              Abbrechen
            </Button>
            <Button
              variant="secondary"
              onClick={handleQuarterlyPreview}
              disabled={qPreviewLoading || qExporting}
              data-testid="quarterly-preview-btn"
            >
              {qPreviewLoading ? "Prüfe…" : "Vorschau"}
            </Button>
            <Button
              onClick={handleQuarterlyPdfExport}
              disabled={qExporting}
              data-testid="quarterly-pdf-export-submit"
            >
              {qExporting ? "Erstelle PDF…" : "PDF herunterladen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog with Date Range */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Rechnungen exportieren
            </DialogTitle>
            <DialogDescription>
              Wählen Sie den Zeitraum für den Export aus
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Von</Label>
                <Input
                  type="date"
                  value={exportDateFrom}
                  onChange={(e) => setExportDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Bis</Label>
                <Input
                  type="date"
                  value={exportDateTo}
                  onChange={(e) => setExportDateTo(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include-cancelled"
                checked={exportIncludeCancelled}
                onCheckedChange={setExportIncludeCancelled}
              />
              <Label htmlFor="include-cancelled" className="text-sm cursor-pointer">
                Stornierte Rechnungen einbeziehen
              </Label>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Der Export enthält alle finalisierten Rechnungen im angegebenen Zeitraum als CSV-Datei.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleExportWithDateRange} disabled={exporting}>
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exportiere...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Exportieren
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="h-5 w-5" />
              {editingInvoice?.is_free_invoice ? "Freitext-Rechnung bearbeiten" : "Rechnung bearbeiten"}
            </DialogTitle>
            <DialogDescription>
              {editingInvoice?.invoice_number} - Nur Entwürfe können bearbeitet werden
            </DialogDescription>
          </DialogHeader>

          {editingInvoice?.is_free_invoice ? (
            /* Free Invoice Edit Form */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Titel</Label>
                <Input
                  id="edit-title"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Titel der Rechnung"
                />
              </div>

              {/* === Ausführungsadresse (Leistungsort) === */}
              <div className="p-3 rounded-lg border bg-slate-50 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Ausführungsadresse (Leistungsort)</Label>
                  <span className="text-xs text-muted-foreground">optional, alle Felder leer = kein zusätzlicher Block</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    placeholder="Firma / Name"
                    value={editForm.execution_address?.name || ""}
                    onChange={(e) => setEditForm(prev => ({
                      ...prev,
                      execution_address: { ...(prev.execution_address || {}), name: e.target.value }
                    }))}
                    data-testid="edit-execution-address-name"
                  />
                  <Input
                    placeholder="Straße & Hausnr."
                    value={editForm.execution_address?.street || ""}
                    onChange={(e) => setEditForm(prev => ({
                      ...prev,
                      execution_address: { ...(prev.execution_address || {}), street: e.target.value }
                    }))}
                    data-testid="edit-execution-address-street"
                  />
                  <Input
                    placeholder="PLZ"
                    value={editForm.execution_address?.zip || ""}
                    onChange={(e) => setEditForm(prev => ({
                      ...prev,
                      execution_address: { ...(prev.execution_address || {}), zip: e.target.value }
                    }))}
                    data-testid="edit-execution-address-zip"
                  />
                  <Input
                    placeholder="Ort"
                    value={editForm.execution_address?.city || ""}
                    onChange={(e) => setEditForm(prev => ({
                      ...prev,
                      execution_address: { ...(prev.execution_address || {}), city: e.target.value }
                    }))}
                    data-testid="edit-execution-address-city"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Positionen</Label>
                  <Button variant="outline" size="sm" onClick={handleAddItem}>
                    <Plus className="h-4 w-4 mr-1" /> Position
                  </Button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {editForm.items.map((item, index) => (
                    <div key={index} className="p-3 border rounded-lg space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={item.description}
                          onChange={(e) => handleUpdateItem(index, "description", e.target.value)}
                          placeholder="Beschreibung"
                          className="flex-1"
                        />
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-xs">Menge</Label>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleUpdateItem(index, "quantity", parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Einheit</Label>
                          <Input
                            value={item.unit}
                            onChange={(e) => handleUpdateItem(index, "unit", e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Einzelpreis €</Label>
                          <Input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => handleUpdateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Gesamt €</Label>
                          <Input
                            value={(item.quantity * item.unit_price).toFixed(2)}
                            disabled
                            className="bg-gray-50"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-payment-days">Zahlungsziel (Tage)</Label>
                  <Input
                    id="edit-payment-days"
                    type="number"
                    value={editForm.payment_days}
                    onChange={(e) => setEditForm(prev => ({ ...prev, payment_days: parseInt(e.target.value) || 14 }))}
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-vat-rate">MwSt.-Satz (%)</Label>
                  <Input
                    id="edit-vat-rate"
                    type="number"
                    value={editForm.vat_rate}
                    onChange={(e) => setEditForm(prev => ({ ...prev, vat_rate: parseFloat(e.target.value) || 19 }))}
                    min="0"
                    max="100"
                    step="0.1"
                    disabled={!editForm.include_vat}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-include-vat"
                  checked={editForm.include_vat}
                  onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, include_vat: checked }))}
                />
                <Label htmlFor="edit-include-vat">Mit Mehrwertsteuer</Label>
              </div>

              <div className="flex items-start space-x-2 p-2 bg-muted/40 rounded">
                <Checkbox
                  id="edit-show-item-prices"
                  checked={editForm.show_item_prices !== false}
                  onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, show_item_prices: !!checked }))}
                  data-testid="edit-show-item-prices"
                />
                <div>
                  <Label htmlFor="edit-show-item-prices">Einzelpreise im PDF ausweisen</Label>
                  <p className="text-xs text-muted-foreground">Aus → PDF zeigt nur Pos./Beschreibung/Menge; Gesamtsumme am Ende.</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-notes-free">Notizen</Label>
                <Textarea
                  id="edit-notes-free"
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Interne Notizen..."
                  rows={3}
                />
              </div>

              {/* Summary */}
              <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Netto:</span>
                  <span>{editForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toFixed(2)} €</span>
                </div>
                {editForm.include_vat && (
                  <div className="flex justify-between text-sm">
                    <span>MwSt. ({editForm.vat_rate}%):</span>
                    <span>{(editForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) * editForm.vat_rate / 100).toFixed(2)} €</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t pt-1">
                  <span>Gesamt:</span>
                  <span>
                    {(editForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) * (1 + (editForm.include_vat ? editForm.vat_rate / 100 : 0))).toFixed(2)} €
                  </span>
                </div>
                {(editForm.external_advances || []).length > 0 && (
                  <>
                    <div className="flex justify-between text-sm pt-1">
                      <span className="text-muted-foreground">Externe Abschläge:</span>
                      <span className="font-medium text-orange-600">
                        -{(editForm.external_advances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0).toFixed(2)} €
                      </span>
                    </div>
                    <div className="flex justify-between font-bold border-t pt-1 text-green-700">
                      <span>Zahlbetrag (Restbetrag):</span>
                      <span>
                        {(
                          (editForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) * (1 + (editForm.include_vat ? editForm.vat_rate / 100 : 0)))
                          - (editForm.external_advances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0)
                        ).toFixed(2)} €
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Externe Abschlagsrechnungen */}
              <div className="space-y-2 rounded-md border border-purple-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Externe Abschlagsrechnungen (Schlussrechnungs-Modus)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="add-free-ext-advance-row"
                    onClick={() => setEditForm(prev => ({
                      ...prev,
                      external_advances: [...(prev.external_advances || []), { invoice_number: "", amount: "", payment_date: "", notes: "" }]
                    }))}
                  >
                    + Abschlagsrechnung hinzufügen
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Erfassen Sie hier bereits außerhalb des CRM gezahlte Abschlagsrechnungen. Sie werden von der Gesamtsumme abgezogen.
                </p>
                {(editForm.external_advances || []).length === 0 ? (
                  <p className="text-xs italic text-gray-500 py-2">Noch keine externen Abschläge erfasst.</p>
                ) : (
                  <div className="space-y-2">
                    {(editForm.external_advances || []).map((adv, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`free-ext-adv-row-${idx}`}>
                        <Input
                          className="col-span-5"
                          placeholder="Rechnungsnummer *"
                          value={adv.invoice_number}
                          data-testid={`free-ext-adv-number-${idx}`}
                          onChange={(e) => setEditForm(prev => {
                            const arr = [...(prev.external_advances || [])];
                            arr[idx] = { ...arr[idx], invoice_number: e.target.value };
                            return { ...prev, external_advances: arr };
                          })}
                        />
                        <Input
                          className="col-span-3"
                          type="number"
                          step="0.01"
                          placeholder="Betrag (brutto) *"
                          value={adv.amount}
                          data-testid={`free-ext-adv-amount-${idx}`}
                          onChange={(e) => setEditForm(prev => {
                            const arr = [...(prev.external_advances || [])];
                            arr[idx] = { ...arr[idx], amount: e.target.value };
                            return { ...prev, external_advances: arr };
                          })}
                        />
                        <Input
                          className="col-span-3"
                          type="date"
                          value={adv.payment_date}
                          data-testid={`free-ext-adv-date-${idx}`}
                          onChange={(e) => setEditForm(prev => {
                            const arr = [...(prev.external_advances || [])];
                            arr[idx] = { ...arr[idx], payment_date: e.target.value };
                            return { ...prev, external_advances: arr };
                          })}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="col-span-1 text-red-600 hover:bg-red-50"
                          data-testid={`free-ext-adv-remove-${idx}`}
                          onClick={() => setEditForm(prev => ({
                            ...prev,
                            external_advances: (prev.external_advances || []).filter((_, i) => i !== idx)
                          }))}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Regular Invoice Edit Form */
            <div className="space-y-4">
              {/* === SCHLUSSRECHNUNG: Spezielle Bearbeitung === */}
              {(editingInvoice?.invoice_type === "schlussrechnung" || editingInvoice?.invoice_type === "schluss") && (
                <div className="space-y-4 p-4 border rounded-lg bg-purple-50 border-purple-200">
                  <h4 className="font-medium text-purple-800 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Schlussrechnung - Betragsanpassung
                  </h4>
                  
                  {/* Warnung bei Überzahlung */}
                  {editForm.has_overpayment_warning && (
                    <div className="p-3 bg-red-100 border border-red-300 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-red-800">
                        <strong>Warnung:</strong> Die Abschlagszahlungen übersteigen den Auftragswert. 
                        Bitte prüfen Sie die Beträge oder passen Sie manuell an.
                      </div>
                    </div>
                  )}
                  
                  {/* Zusammenfassung */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Auftragswert (Gesamt):</span>
                      <span className="font-medium">{editForm.offer_total_gross?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Geleistete Abschläge (CRM):</span>
                      <span className="font-medium text-orange-600">-{editForm.previous_payments?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Externe Abschläge:</span>
                      <span className="font-medium text-orange-600">
                        -{(editForm.external_advances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                      </span>
                    </div>
                    <div className="flex justify-between col-span-2 pt-2 border-t">
                      <span className="font-medium">Automatischer Restbetrag:</span>
                      <span className={`font-bold ${(editForm.offer_total_gross - editForm.previous_payments - (editForm.external_advances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0)) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {(editForm.offer_total_gross - editForm.previous_payments - (editForm.external_advances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0))?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                      </span>
                    </div>
                  </div>

                  {/* Externe Abschlagsrechnungen */}
                  <div className="space-y-2 rounded-md border border-purple-200 bg-white p-3">
                    <div className="flex items-center justify-between">
                      <Label className="font-medium">Externe Abschlagsrechnungen</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="add-external-advance-row"
                        onClick={() => setEditForm(prev => ({
                          ...prev,
                          external_advances: [...(prev.external_advances || []), { invoice_number: "", amount: "", payment_date: "", notes: "" }]
                        }))}
                      >
                        + Abschlagsrechnung hinzufügen
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Erfassen Sie hier bereits außerhalb des CRM gezahlte Abschlagsrechnungen. Sie werden von der Gesamtsumme abgezogen.
                    </p>
                    {(editForm.external_advances || []).length === 0 ? (
                      <p className="text-xs italic text-gray-500 py-2">Noch keine externen Abschläge erfasst.</p>
                    ) : (
                      <div className="space-y-2">
                        {(editForm.external_advances || []).map((adv, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`ext-adv-row-${idx}`}>
                            <Input
                              className="col-span-5"
                              placeholder="Rechnungsnummer *"
                              value={adv.invoice_number}
                              data-testid={`ext-adv-number-${idx}`}
                              onChange={(e) => setEditForm(prev => {
                                const arr = [...(prev.external_advances || [])];
                                arr[idx] = { ...arr[idx], invoice_number: e.target.value };
                                return { ...prev, external_advances: arr };
                              })}
                            />
                            <Input
                              className="col-span-3"
                              type="number"
                              step="0.01"
                              placeholder="Betrag (brutto) *"
                              value={adv.amount}
                              data-testid={`ext-adv-amount-${idx}`}
                              onChange={(e) => setEditForm(prev => {
                                const arr = [...(prev.external_advances || [])];
                                arr[idx] = { ...arr[idx], amount: e.target.value };
                                return { ...prev, external_advances: arr };
                              })}
                            />
                            <Input
                              className="col-span-3"
                              type="date"
                              value={adv.payment_date}
                              data-testid={`ext-adv-date-${idx}`}
                              onChange={(e) => setEditForm(prev => {
                                const arr = [...(prev.external_advances || [])];
                                arr[idx] = { ...arr[idx], payment_date: e.target.value };
                                return { ...prev, external_advances: arr };
                              })}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="col-span-1 text-red-600 hover:bg-red-50"
                              data-testid={`ext-adv-remove-${idx}`}
                              onClick={() => setEditForm(prev => ({
                                ...prev,
                                external_advances: (prev.external_advances || []).filter((_, i) => i !== idx)
                              }))}
                            >
                              ✕
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Berechnungsmodus */}
                  <div className="space-y-2">
                    <Label>Berechnungsmodus</Label>
                    <Select 
                      value={editForm.calculation_mode} 
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, calculation_mode: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automatische Berechnung (Original-Auftragswert)</SelectItem>
                        <SelectItem value="manual">Manueller Gesamtbetrag (z.B. nach Nachlass)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Manueller GESAMTBETRAG */}
                  {editForm.calculation_mode === "manual" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="manual-total-amount">
                          Neuer maßgeblicher Gesamtbetrag (Brutto)
                        </Label>
                        <Input
                          id="manual-total-amount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={editForm.manual_total_amount}
                          onChange={(e) => {
                            const newTotal = parseFloat(e.target.value) || 0;
                            const newRemaining = newTotal - editForm.previous_payments;
                            setEditForm(prev => ({ 
                              ...prev, 
                              manual_total_amount: e.target.value,
                              // Zeige Vorschau des berechneten Zahlbetrags
                              calculated_remaining: newRemaining
                            }));
                          }}
                          placeholder={`z.B. ${(editForm.offer_total_gross - 500).toLocaleString('de-DE', { minimumFractionDigits: 2 })}`}
                        />
                        <p className="text-xs text-muted-foreground">
                          Original-Auftragswert: {editForm.offer_total_gross?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                        </p>
                        
                        {/* Vorschau der Berechnung */}
                        {editForm.manual_total_amount && parseFloat(editForm.manual_total_amount) > 0 && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                            <div className="flex justify-between">
                              <span>Neuer Gesamtbetrag:</span>
                              <span className="font-medium">{parseFloat(editForm.manual_total_amount).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                            </div>
                            <div className="flex justify-between text-orange-600">
                              <span>- Geleistete Abschläge:</span>
                              <span>{editForm.previous_payments?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                            </div>
                            <div className="flex justify-between font-bold pt-1 border-t mt-1">
                              <span>= Zahlbetrag:</span>
                              <span className={(parseFloat(editForm.manual_total_amount) - editForm.previous_payments) < 0 ? 'text-red-600' : 'text-green-600'}>
                                {(parseFloat(editForm.manual_total_amount) - editForm.previous_payments).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                              </span>
                            </div>
                            {(parseFloat(editForm.manual_total_amount) - editForm.previous_payments) < 0 && (
                              <div className="mt-2 text-red-600 text-xs flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Achtung: Abschläge übersteigen den Gesamtbetrag!
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="manual-adjustment-reason">Begründung</Label>
                        <Select 
                          value={editForm.manual_adjustment_reason || ""} 
                          onValueChange={(value) => setEditForm(prev => ({ ...prev, manual_adjustment_reason: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Grund auswählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Nachträglicher Nachlass">Nachträglicher Nachlass</SelectItem>
                            <SelectItem value="Kulanz">Kulanz</SelectItem>
                            <SelectItem value="Preisbereinigung">Preisbereinigung</SelectItem>
                            <SelectItem value="Vereinbarte Anpassung">Vereinbarte Anpassung</SelectItem>
                            <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                          </SelectContent>
                        </Select>
                        <Textarea
                          id="manual-adjustment-notes"
                          value={editForm.manual_adjustment_reason?.startsWith('Sonstiges') ? editForm.manual_adjustment_reason : ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, manual_adjustment_reason: e.target.value }))}
                          placeholder="Zusätzliche Notizen zur Anpassung..."
                          rows={2}
                          className="mt-2"
                        />
                      </div>
                    </>
                  )}

                  {/* === „Nullbetrag"-Schlussrechnung (§35a EStG) === */}
                  <div className="mt-2 p-3 rounded-lg border bg-amber-50 border-amber-200 space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                        checked={!!editForm.force_zero_balance}
                        onChange={(e) => setEditForm(prev => ({ ...prev, force_zero_balance: e.target.checked }))}
                        data-testid="edit-invoice-force-zero-balance"
                      />
                      <span className="text-sm">
                        <span className="font-medium">Restbetrag auf 0 € setzen</span>{" "}
                        <span className="text-muted-foreground">
                          – Der Kunde hat bereits alle Abschläge bezahlt. Die Schlussrechnung dient nur zur Dokumentation und zum §35a-EStG-Lohnkostennachweis.
                        </span>
                      </span>
                    </label>
                    {editForm.force_zero_balance && (
                      <div className="flex items-center gap-2 pt-1 pl-6">
                        <Label htmlFor="labor-cost-pct" className="text-xs whitespace-nowrap">
                          Lohnkostenanteil (%):
                        </Label>
                        <Input
                          id="labor-cost-pct"
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={editForm.labor_cost_percentage ?? ""}
                          onChange={(e) => setEditForm(prev => ({ ...prev, labor_cost_percentage: e.target.value }))}
                          placeholder="Standard aus Firma (30%)"
                          className="w-40 h-8 text-sm"
                          data-testid="edit-invoice-labor-cost-pct"
                        />
                        <span className="text-xs text-muted-foreground">
                          leer lassen → Firma-Default wird verwendet
                        </span>
                      </div>
                    )}
                  </div>

                  {/* === SCHLUSSAUFLISTUNG: Bereits gestellte Rechnungen manuell bearbeiten === */}
                  <div className="mt-2 p-3 rounded-lg border bg-slate-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Bereits gestellte Rechnungen (Auflistung)</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditForm(prev => ({
                          ...prev,
                          previous_invoices_snapshot: [
                            ...(prev.previous_invoices_snapshot || []),
                            { invoice_number: "", invoice_date: "", gross_total: "", invoice_type: "abschlagsrechnung" }
                          ]
                        }))}
                        data-testid="edit-snapshot-add-row"
                      >
                        + Zeile hinzufügen
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Nummer, Datum und Betrag können manuell überschrieben werden. Der §35a-EStG-Nachweis
                      basiert auf der <strong>Summe dieser Beträge</strong>.
                    </p>
                    {(editForm.previous_invoices_snapshot || []).length === 0 ? (
                      <div className="text-xs text-muted-foreground italic p-2">
                        Keine Einträge – die Auflistung ist leer.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(editForm.previous_invoices_snapshot || []).map((entry, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                            <Input
                              className="col-span-3 h-8 text-sm"
                              placeholder="RE-Nummer"
                              value={entry.invoice_number || ""}
                              onChange={(e) => setEditForm(prev => {
                                const arr = [...(prev.previous_invoices_snapshot || [])];
                                arr[idx] = { ...arr[idx], invoice_number: e.target.value };
                                return { ...prev, previous_invoices_snapshot: arr };
                              })}
                              data-testid={`edit-snapshot-number-${idx}`}
                            />
                            <Input
                              className="col-span-3 h-8 text-sm"
                              type="date"
                              value={entry.invoice_date || ""}
                              onChange={(e) => setEditForm(prev => {
                                const arr = [...(prev.previous_invoices_snapshot || [])];
                                arr[idx] = { ...arr[idx], invoice_date: e.target.value };
                                return { ...prev, previous_invoices_snapshot: arr };
                              })}
                              data-testid={`edit-snapshot-date-${idx}`}
                            />
                            <Select
                              value={entry.invoice_type || "abschlagsrechnung"}
                              onValueChange={(v) => setEditForm(prev => {
                                const arr = [...(prev.previous_invoices_snapshot || [])];
                                arr[idx] = { ...arr[idx], invoice_type: v };
                                return { ...prev, previous_invoices_snapshot: arr };
                              })}
                            >
                              <SelectTrigger className="col-span-2 h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="abschlagsrechnung">Abschlag</SelectItem>
                                <SelectItem value="schlussrechnung">Schluss</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              className="col-span-3 h-8 text-sm text-right"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Betrag"
                              value={entry.gross_total || ""}
                              onChange={(e) => setEditForm(prev => {
                                const arr = [...(prev.previous_invoices_snapshot || [])];
                                arr[idx] = { ...arr[idx], gross_total: e.target.value };
                                return { ...prev, previous_invoices_snapshot: arr };
                              })}
                              data-testid={`edit-snapshot-amount-${idx}`}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="col-span-1 h-8 p-0 text-red-600"
                              onClick={() => setEditForm(prev => ({
                                ...prev,
                                previous_invoices_snapshot: (prev.previous_invoices_snapshot || []).filter((_, i) => i !== idx)
                              }))}
                              data-testid={`edit-snapshot-remove-${idx}`}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                        <div className="flex justify-end pt-1 text-xs text-muted-foreground border-t">
                          <span>
                            Summe:&nbsp;
                            <strong className="tabular-nums">
                              {((editForm.previous_invoices_snapshot || []).reduce((s, e) => s + (parseFloat(e.gross_total) || 0), 0))
                                .toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                            </strong>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Standard Betragskorrektur - für NICHT-Schlussrechnungen */}
              {editingInvoice?.invoice_type !== "schlussrechnung" && editingInvoice?.invoice_type !== "schluss" && (
                <div className="space-y-2">
                  <Label htmlFor="edit-gross-total" className="flex items-center gap-2">
                    Rechnungsbetrag (Brutto) korrigieren
                    <span className="text-xs text-muted-foreground">
                      Aktuell: {editForm.current_gross_total?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                    </span>
                  </Label>
                  <Input
                    id="edit-gross-total"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.gross_total_override}
                    onChange={(e) => setEditForm(prev => ({ ...prev, gross_total_override: e.target.value }))}
                    placeholder={`Neuer Betrag eingeben (leer lassen = ${editForm.current_gross_total?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €)`}
                  />
                  <p className="text-xs text-muted-foreground">
                    Nur ausfüllen, wenn Sie den Betrag manuell korrigieren möchten. Der Nettobetrag wird automatisch angepasst.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit-delivery-date">Lieferdatum</Label>
                <Input
                  id="edit-delivery-date"
                  type="date"
                  value={editForm.delivery_date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, delivery_date: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-payment-due-days">Zahlungsziel (Tage)</Label>
                <Input
                  id="edit-payment-due-days"
                  type="number"
                  value={editForm.payment_due_days}
                  onChange={(e) => setEditForm(prev => ({ ...prev, payment_due_days: parseInt(e.target.value) || 14 }))}
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notizen</Label>
                <Textarea
                  id="edit-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Interne Notizen zur Rechnung..."
                  rows={4}
                />
              </div>

              {/* MwSt-Ausweis Toggle: nur für Non-PV Rechnungen */}
              {(() => {
                const items = editingInvoice?.line_items || [];
                const hasPvSemantics = items.some(it =>
                  ["pv_system", "wallbox", "storage", "inverter"].includes(it.component_type)
                );
                if (hasPvSemantics) return null;
                return (
                  <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg" data-testid="edit-invoice-vat-toggle-wrapper">
                    <div>
                      <Label htmlFor="edit-invoice-include-vat" className="cursor-pointer font-medium">MwSt in Rechnung ausweisen</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Aus: reine Nettorechnung. An: MwSt-Satz aus Firmen-CI wird verwendet.
                        Der Bruttobetrag bleibt unverändert – Netto und MwSt werden neu aufgeteilt.
                      </p>
                    </div>
                    <Switch
                      id="edit-invoice-include-vat"
                      checked={editForm.include_vat_override !== false}
                      onCheckedChange={(v) => setEditForm(prev => ({ ...prev, include_vat_override: v }))}
                      data-testid="edit-invoice-include-vat-switch"
                    />
                  </div>
                );
              })()}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                "Speichern"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
